import { useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { IconButton, KiteMark, PhoneNumber, useTheme, Button as VdButton } from "@vd/ui";
import { ArrowUpRight, Moon, PhoneOff, Sun } from "lucide-react";
import { useEffect, useMemo } from "react";
import { meQuery } from "../api/admin";
import { listenQuery } from "../api/calls";
import { ApiError } from "../api/client";
import type { CallDetail, Segment } from "../api/types";
import { RecordingPlayer, useRecording } from "../components/RecordingPlayer";
import { Badge, Card, cx, EmptyState, ErrorState, Loading } from "../components/ui";
import { fmtPhone } from "../lib/format";
import { dispositionName } from "../lib/labels";
import { activeSegment, clock, finalSegments } from "../lib/listen";
import { useNavigate } from "../lib/navigate";
import { recordingState } from "../lib/postCall";

// Opened from the "Listen to the recording" link in a CRM note. Outside the admin shell, so any
// member who may see the call (its rep, the brand's managers, admins) can use it.
export const Route = createFileRoute("/listen/$callId")({
  staticData: { crumb: { label: "Recording", parents: [] } },
  beforeLoad: async ({ context, location }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        throw redirect({ to: "/signin", search: { redirect: location.href } });
      }
      throw e;
    }
  },
  component: ListenPage,
});

const whenFmt = new Intl.DateTimeFormat(undefined, {
  weekday: "short",
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

function ListenPage() {
  const { callId } = Route.useParams();
  const q = useQuery(listenQuery(callId));
  const navigate = useNavigate();
  const unauthorized = q.error instanceof ApiError && q.error.status === 401;

  useEffect(() => {
    if (unauthorized) {
      navigate({ to: "/signin", search: { redirect: `/listen/${callId}` }, replace: true });
    }
  }, [unauthorized, navigate, callId]);

  let body: React.ReactNode;
  if (q.isPending || unauthorized) body = <Loading />;
  else if (q.isError) {
    body =
      q.error instanceof ApiError && q.error.status === 404 ? (
        <EmptyState
          icon={<PhoneOff />}
          title="This call isn't available"
          description="It belongs to another rep, or it was removed."
        />
      ) : (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      );
  } else body = <Listen call={q.data} />;

  return (
    <div className="min-h-full bg-canvas">
      <TopBar />
      <main className="mx-auto w-full max-w-[760px] px-4 pb-16 sm:px-6">{body}</main>
    </div>
  );
}

function TopBar() {
  const { resolved, setTheme } = useTheme();
  return (
    <header className="mx-auto flex h-14 w-full max-w-[760px] items-center justify-between px-4 sm:px-6">
      <span className="flex items-center gap-2">
        <KiteMark size={20} />
        <span className="font-medium text-ui">Kite</span>
      </span>
      <IconButton
        label={resolved === "dark" ? "Light theme" : "Dark theme"}
        icon={resolved === "dark" ? <Sun /> : <Moon />}
        onClick={() => setTheme(resolved === "dark" ? "light" : "dark")}
      />
    </header>
  );
}

function Listen({ call }: { call: CallDetail }) {
  const other = call.direction === "outbound" ? call.to : call.from;
  const contactName = call.contact?.name || fmtPhone(other);
  const repName = call.rep_name || "Rep";
  const segments = useMemo(() => finalSegments(call.transcript), [call.transcript]);
  const vm = call.voicemail;
  const rec = useRecording(call.id, vm?.duration_s ?? call.duration_s ?? 0);
  const started = new Date(call.started_at);
  const steps = call.summary?.next_steps.filter((s) => s.trim()) ?? [];

  useSpaceToToggle(call.recording_available ? rec.toggle : null);

  const facts: [string, React.ReactNode][] = [
    ["Date", Number.isNaN(started.getTime()) ? call.started_at : whenFmt.format(started)],
    vm
      ? ["Message", vm.duration_s !== undefined ? clock(vm.duration_s) : "None"]
      : ["Duration", call.duration_s !== undefined ? clock(call.duration_s) : "None"],
    [vm ? "Left for" : "Rep", vm?.shared ? "Shared number" : repName],
  ];

  return (
    <>
      <section className="pt-6 pb-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="font-display font-medium text-display">{contactName}</h1>
            <p className="mt-1 text-body text-ink-2">
              {call.contact?.company}
              {call.contact?.company && call.contact?.name ? " · " : null}
              {call.contact?.name ? <PhoneNumber e164={other} types={call.phone_types} /> : null}
            </p>
          </div>
          {call.crm_url ? (
            <VdButton asChild size="sm" variant="secondary">
              <a href={call.crm_url} target="_blank" rel="noopener noreferrer">
                Open in Odoo
                <ArrowUpRight />
              </a>
            </VdButton>
          ) : null}
        </div>
        <dl className="mt-5 flex flex-wrap gap-x-8 gap-y-3">
          {facts.map(([k, v]) => (
            <div key={k}>
              <dt className="text-caption text-ink-3">{k}</dt>
              <dd className="mt-0.5 text-ui tnum">{v}</dd>
            </div>
          ))}
          <div>
            <dt className="text-caption text-ink-3">Disposition</dt>
            <dd className="mt-0.5 flex items-center gap-1.5">
              <Badge>{call.direction === "outbound" ? "Outbound" : "Inbound"}</Badge>
              {vm ? <Badge tone="accent">Voicemail</Badge> : null}
              {call.disposition ? (
                <Badge>{dispositionName(call.disposition, null, call.disposition_label)}</Badge>
              ) : null}
            </dd>
          </div>
        </dl>
      </section>

      <div className="flex flex-col gap-4">
        <Card className="rounded-2xl">
          <div className="p-5">
            {call.recording_available ? (
              <>
                <RecordingPlayer rec={rec} />
                {rec.error ? (
                  <p role="alert" className="mt-3 text-ui text-danger-ink">
                    {rec.error}
                  </p>
                ) : null}
              </>
            ) : recordingState(call) === "processing" ? (
              <p role="status" className="text-ui text-ink-2">
                The recording is processing. It appears here when it's ready.
              </p>
            ) : (
              <p className="text-ui text-ink-2">There is no recording for this call.</p>
            )}
          </div>
        </Card>

        {vm?.summary && !call.summary ? (
          <Card title="Voicemail">
            <p className="px-4 py-3 text-body">{vm.summary}</p>
          </Card>
        ) : null}
        {call.summary ? (
          <Card title={vm ? "Voicemail" : "Summary"}>
            <div className="px-4 py-3 text-body">
              <p>{call.summary.summary}</p>
              {steps.length ? (
                <>
                  <h3 className="mt-4 mb-1 font-medium text-caption text-ink-2">Next steps</h3>
                  <ul className="list-disc space-y-1 pl-5">
                    {steps.map((s) => (
                      <li key={s}>{s}</li>
                    ))}
                  </ul>
                </>
              ) : null}
            </div>
          </Card>
        ) : null}

        <Card title="Transcript">
          {segments.length === 0 ? (
            <EmptyState title={call.processing ? "Transcript processing" : "No transcript"} />
          ) : (
            <Transcript
              segments={segments}
              names={{ rep: repName, customer: contactName }}
              activeMs={rec.playing || rec.time > 0 ? rec.time * 1000 : -1}
              onSeek={call.recording_available ? (ms) => rec.playFrom(ms / 1000) : null}
            />
          )}
        </Card>
      </div>
    </>
  );
}

function Transcript({
  segments,
  names,
  activeMs,
  onSeek,
}: {
  segments: Segment[];
  names: Record<Segment["speaker"], string>;
  activeMs: number;
  onSeek: ((ms: number) => void) | null;
}) {
  const active = activeMs < 0 ? -1 : activeSegment(segments, activeMs);
  return (
    <ol className="px-2 py-2">
      {segments.map((s, i) => {
        const first = i === 0 || segments[i - 1]!.speaker !== s.speaker;
        const time = clock(s.start_ms / 1000);
        return (
          <li
            key={`${s.start_ms}-${s.speaker}`}
            aria-current={i === active ? "true" : undefined}
            className={cx(
              "grid grid-cols-[48px_1fr] gap-x-2 rounded-md px-2 transition-colors duration-150",
              first && i > 0 ? "mt-3" : "",
              i === active ? "bg-fill-1" : "",
            )}
          >
            {onSeek ? (
              <button
                type="button"
                onClick={() => onSeek(s.start_ms)}
                aria-label={`Play from ${time}`}
                className={cx(
                  "self-start rounded-sm py-1 text-left text-caption leading-5 tnum hover:text-accent-ink",
                  first ? "mt-4" : "",
                  i === active ? "text-accent-ink" : "text-ink-3",
                )}
              >
                {time}
              </button>
            ) : (
              <span className={cx("py-1 text-caption text-ink-3 leading-5 tnum", first ? "mt-4" : "")}>
                {time}
              </span>
            )}
            <div className="min-w-0 py-1">
              {first ? (
                <p
                  className={cx(
                    "font-medium text-caption",
                    s.speaker === "rep" ? "text-speaker-rep" : "text-speaker-customer",
                  )}
                >
                  {names[s.speaker]}
                </p>
              ) : null}
              <p className="text-body">{s.text}</p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/** Space plays and pauses unless focus is on something that uses the key itself. */
function useSpaceToToggle(toggle: (() => void) | null) {
  useEffect(() => {
    if (!toggle) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== " " || e.repeat || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t?.closest("button, a, input, textarea, select, [contenteditable]")) return;
      e.preventDefault();
      toggle();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [toggle]);
}
